import { Context, createContext, MutableRefObject, useContext, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { FeaturesConfigDTO } from '../../../NonReact/Types/Types'
import { JSONResponse } from '../../../NonReact/common/Types/Types'
import { getFeaturesConfig } from '../../Common/API/GenericConfigAPI'
import { ReactState } from '../../Common/Contexts'
import { BrandingFeatureFlagPredicate, createBrandingFeatureFlagPredicate } from './BrandingFeatureFlagUtils'

/**
 * Supplies the branding form's feature flag predicate to every tab, sub-area and field (including list sub-entries).
 * The default hides every flagged item, so a form rendered outside a provider never shows a dark feature.
 */
export const BrandingFeatureFlagContext: Context<BrandingFeatureFlagPredicate> =
    createContext<BrandingFeatureFlagPredicate>(createBrandingFeatureFlagPredicate(null))

/**
 * Loads `features.yaml` once and returns a predicate for the branding form's `featureFlag` keys.
 * Flagged items stay hidden until the config has loaded, and for good if it fails to load.
 *
 * @returns The feature flag predicate.
 */
export const useBrandingFeatureFlagPredicate = (): BrandingFeatureFlagPredicate => {
    const [featuresConfig, setFeaturesConfig]: ReactState<Nullable<FeaturesConfigDTO>> =
        useState<Nullable<FeaturesConfigDTO>>(null)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const predicate: BrandingFeatureFlagPredicate = useMemo(
        (): BrandingFeatureFlagPredicate => createBrandingFeatureFlagPredicate(featuresConfig),
        [featuresConfig]
    )

    useEffect((): void => {
        getFeaturesConfig()
            .then((response: JSONResponse<FeaturesConfigDTO>): void => {
                if (isMounted.current && response) {
                    setFeaturesConfig(response)
                }
            })
            .catch((): void => {
                // Leave flagged items hidden when the features config cannot be loaded.
            })
    }, [])

    return predicate
}

/**
 * Returns the branding form's feature flag predicate from {@link BrandingFeatureFlagContext}.
 *
 * @returns The feature flag predicate.
 */
export const useBrandingFeatureFlag = (): BrandingFeatureFlagPredicate => useContext(BrandingFeatureFlagContext)
